import { useQuery } from '@tanstack/react-query'

import type { UseQueryResult } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import type { IAppSummary } from '~/types/application'

import { getAppSummary, getAppSummaryByAppKeyDeviceType } from '~/api/application-summary'
import { getAllApps } from '~/api/apps'
import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'

////////// KEYS //////////

export const appsKeys = {
  all: ['applications-data'] as const,
  appsByAppKeyList: (appKey?: string) => [...appsKeys.all, 'apps-by-app-key-list', appKey] as const,
  details: (appId?: string) => [...appsKeys.all, 'details', appId] as const,
  detailsByAppKeyDeviceType: (appKey?: string, deviceType?: string) =>
    [...appsKeys.all, 'detailsByAppKeyDeviceType', appKey, deviceType] as const,
  list: (owned?: boolean, params?: AxiosRequestConfig['params']) => [...appsKeys.all, 'list', owned, params] as const,
}

////////// QUERIES //////////

export const useAppSummaryDetailsQuery = (appId?: string, enabled = true): UseQueryResult<IAppSummary, unknown> => {
  return useQuery({
    enabled: enabled && !!appId,
    queryFn: () => getAppSummary(appId as string),
    queryKey: appsKeys.details(appId),
    refetchOnMount: false,
  })
}

// NOTE: To remove
export const useAppSummaryDetailsQueryV2 = (
  appKey?: string,
  deviceType?: string,
  enabled = true
): UseQueryResult<IAppSummary, unknown> => {
  return useQuery({
    enabled: enabled && !!appKey && !!deviceType,
    queryFn: () => getAppSummaryByAppKeyDeviceType(appKey as string, deviceType as string),
    queryKey: appsKeys.detailsByAppKeyDeviceType(appKey, deviceType),
    refetchOnMount: false,
  })
}

/** @deprecated use useAppsManagementListQuery instead */
export const useAppsListQuery = ({
  owned,
  params,
}: {
  owned?: boolean
  params?: AxiosRequestConfig['params']
}): UseQueryResult<IPaginatedResponse<IAppSummary>, unknown> => {
  return useQuery({
    queryFn: () =>
      getAllApps({
        owned,
        page: params?.page || 0,
        query: params?.query,
        size: params?.size || DEFAULT_ITEMS_PER_PAGE_VALUE,
      }),
    queryKey: appsKeys.list(owned, params),
    refetchOnMount: false,
  })
}
